import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Paper,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table,
  Checkbox,
  TableSortLabel,
  Box,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import SiPagination from "./pagination";
import "./index.scss";
import DataExport from "./data-export";
const CustomTable = ({
  header, // Table headers array
  data, // Table data array
  onClick, // This is the onClick function which is passed in "render" callback function
  pageCount, // This should be count of total number of pages
  filters, // This will be object of filters, which can contain page number etc.
  onChange, // This onChange will be a function which will be called on page change
  enableSorting = true, // For enabling sorting feature
  enableSelection = false, // For adding checkbox for selection,
  fetchAllData, // Function to call for fetching the data to export it in excel file
  excelFileName = "data", // Excel file name to be downloaded
  downloadReport = false,
  filterCom,
  isLoading = false,
}) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("");
  const [selected, setSelected] = useState([]);
  const tableRef = useRef();
  // Update the page number based on filters passed as props
  useEffect(() => {
    if (Number.isInteger(parseInt(filters?.page))) {
      setPage(filters.page);
    }
  }, [filters]);

  // Handle sorting when a column header is clicked
  const handleRequestSort = (event, property) => {
    if (!enableSorting) return; // Disable sorting if not enabled
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle select all checkbox click
  const handleSelectAllClick = (event) => {
    if (!enableSelection) return; // Disable selection if not enabled
    if (event.target.checked) {
      const newSelected = data.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  // Handle individual row selection by clicking the checkbox
  const handleClick = (event, id) => {
    if (!enableSelection) return; // Disable selection if not enabled
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  // Check if a row is selected based on its id
  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Sort the data array based on the selected comparator
  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Get comparator function based on sorting order and column
  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => (b[orderBy] < a[orderBy] ? -1 : 1)
      : (a, b) => (a[orderBy] < b[orderBy] ? -1 : 1);
  };

  // Memoize the sorted data to prevent unnecessary re-renders
  const sortedData = useMemo(
    () =>
      enableSorting ? stableSort(data, getComparator(order, orderBy)) : data,
    [data, order, orderBy, enableSorting]
  );
  const renderSkeleton = () => {
    return Array.from(new Array(6)).map((_, index) => (
      <TableRow key={index}>
        {enableSelection && (
          <TableCell padding="checkbox">
            <Skeleton variant="rectangular" width={24} height={24} />
          </TableCell>
        )}
        {header.map((col, colIndex) => (
          <TableCell key={colIndex}>
            <Skeleton variant="rectangular" width="80%" height={24} />
          </TableCell>
        ))}
      </TableRow>
    ));
  };
  // Determine if the table has grouped headers
  const hasGroupedHeader = header.some((item) => item.columns);

  return (
    <div className="main-custom-table-con">
      <Paper sx={{ width: "100%", mb: 2, boxShadow: "none" }}>
        <TableContainer
          component={Paper}
          sx={{ overflow: "hidden", boxShadow: "0px 0px 4px 0px #00000026" }}
        >
          {filterCom && filterCom}
          <div className="scroll-table">
            <Table
              // sx={{ minWidth: 650 }}
              aria-label="custom table"
              stickyHeader
              ref={tableRef}
            >
              <TableHead>
                <TableRow>
                  {enableSelection && (
                    <TableCell
                      padding="checkbox"
                      rowSpan={hasGroupedHeader ? 2 : 1}
                    >
                      <Checkbox
                        color="primary"
                        indeterminate={
                          selected.length > 0 && selected.length < data.length
                        }
                        checked={
                          data.length > 0 && selected.length === data.length
                        }
                        onChange={handleSelectAllClick}
                      />
                    </TableCell>
                  )}

                  {header.map((head) => (
                    <TableCell
                      key={head.id}
                      align={head.columns ? "center" : "left"}
                      className={`table-head-cell`}
                      colSpan={head.columns ? head.columns.length : 1}
                      rowSpan={head.columns ? 1 : 2}
                      sortDirection={orderBy === head.id ? order : false}
                      style={head?.columns && { borderBottom: "0px" }}
                    >
                      {enableSorting ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : "asc"}
                          onClick={(event) => handleRequestSort(event, head.id)}
                        >
                          <span>
                            {head.label}
                            {orderBy === head.id ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </span>
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}

                      {head?.columns && (
                        <div className="group-header-border"></div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {hasGroupedHeader && (
                  <TableRow>
                    {header.map((head) =>
                      head.columns
                        ? head.columns.map((subCol) => (
                            <TableCell
                              key={subCol.id}
                              align="center"
                              className={`sub-table-head-cell`}
                            >
                              {subCol.label}
                            </TableCell>
                          ))
                        : null
                    )}
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {isLoading
                  ? renderSkeleton()
                  : sortedData.map((ele, mainIndex) => {
                      const isItemSelected = isSelected(ele.id);
                      const labelId = `custom-table-checkbox-${mainIndex}`;

                      return (
                        <TableRow
                          key={ele.id ? ele.id : mainIndex}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          selected={isItemSelected}
                        >
                          {enableSelection && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                inputProps={{
                                  "aria-labelledby": labelId,
                                }}
                                onClick={(event) => handleClick(event, ele.id)}
                              />
                            </TableCell>
                          )}
                          {header.map((col, index) => {
                            if (col.columns) {
                              return col.columns.map((subCol) => (
                                <TableCell
                                  key={subCol.id || `${index}-${subCol.id}`}
                                  align="center"
                                  padding="none"
                                  className="table-body-cell"
                                >
                                  {subCol.render
                                    ? subCol.render(ele)
                                    : ele[col.id]?.map((item) => (
                                        <div key={item._id}>
                                          {item[subCol.id]?.includes("T")
                                            ? item[subCol.id]?.split("T")[0]
                                            : item[subCol.id]}
                                        </div>
                                      ))}
                                </TableCell>
                              ));
                            }

                            return (
                              <TableCell
                                key={col.id || index}
                                // align="center"
                                padding="none"
                                className="table-body-cell"
                              >
                                {col.render ? (
                                  col.render(ele, onClick)
                                ) : ele[col.id]?.length > 50 ? (
                                  <Tooltip title={ele[col.id]}>
                                    {ele[col.id]?.slice(0, 35)} ...
                                  </Tooltip>
                                ) : (
                                  ele[col.id]
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
              </TableBody>
            </Table>
            {!isLoading && data.length === 0 && (
              <div className="no-record-text">
                <p>No Records</p>
              </div>
            )}
          </div>
          <div
            className={
              downloadReport ? "pagination-export-con" : "pagination-con"
            }
          >
            {downloadReport && (
              <DataExport
                header={header}
                data={data}
                fetchAllData={fetchAllData}
                fileName={excelFileName}
                tableRef={tableRef}
              />
            )}
            {!isLoading && (
              <SiPagination
                pageCount={pageCount}
                page={page}
                setPage={setPage}
                onChange={onChange}
              />
            )}
          </div>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default CustomTable;
